/* HUENIT LAB 2.0 — Admin: 설치 통계.
   버전별 설치파일 "다운로드 수" + "가장 최근 다운로드(설치 시도) 시각"을 보여준다.
   ★ 데이터 출처 = S3 액세스 로그를 집계 Lambda 가 DynamoDB 에 적재 → 관리 API GET /install-stats.
     앱은 어차피 설치파일을 내려받으므로 앱 수정 없이 집계된다(업데이트 경로 무변경).
     단위는 "사람"이 아니라 "다운로드"(재시도 포함 근사치) — 앱에 익명 설치ID가 없기 때문.
   관리자 전용(기존 Cognito JWT 인증 그대로). 집계 백엔드 미배포면 안내 상태로 표시. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '../../icons/Icon.jsx';
import { useToast, Loading, ErrorState, EmptyState } from '../../components/index.js';
import { useSession } from '../../auth/session.jsx';
import { getInstallStats } from '../../api/adminApi.js';
import { fmtDateTime } from '../../lib/datetime.js';

const TABS = [
  { value: 'app', label: '앱', icon: 'monitor' },
  { value: 'firmware', label: '펌웨어', icon: 'storage' },
];

const nf = new Intl.NumberFormat('ko-KR');

/* 최근 다운로드(last_at) 역순 — "가장 최근 설치 시도"가 위로. */
const byRecent = (a, b) => String(b.last_at || '').localeCompare(String(a.last_at || ''));

function StatTile({ label, value, sub }) {
  return (
    <div style={{ flex: 1, minWidth: 150, padding: '14px 16px', background: 'var(--white)', border: '1px solid var(--ink-200)', borderRadius: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-400)' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--ink-900)', marginTop: 4, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--ink-400)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

export function InstallStatsAdmin() {
  const toast = useToast();
  const { getIdToken, logout } = useSession();

  const [tab, setTab] = useState('app');
  const [data, setData] = useState(null);   // { app, firmware, generated_at, notReady }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setData(await getInstallStats(getIdToken));
    } catch (e) {
      setError({ message: e.message, status: e.status });
    } finally {
      setLoading(false);
    }
  }, [getIdToken]);

  useEffect(() => { reload(); }, [reload]);

  const rows = useMemo(() => {
    const list = (data && data[tab]) || [];
    return [...list].sort(byRecent);
  }, [data, tab]);

  const totals = useMemo(() => {
    const sum = rows.reduce((n, r) => n + (Number(r.downloads) || 0), 0);
    const recent = rows.map(r => r.last_at).filter(Boolean).sort((a, b) => b.localeCompare(a))[0] || null;
    return { sum, versions: rows.length, recent };
  }, [rows]);

  const maxDl = useMemo(() => rows.reduce((m, r) => Math.max(m, Number(r.downloads) || 0), 0), [rows]);
  const isFw = tab === 'firmware';

  return (
    <div>
      <div className="toolbar">
        <div className="seg">
          {TABS.map(t => (
            <button key={t.value} type="button" className={"seg__opt" + (tab === t.value ? " is-on" : "")} onClick={() => setTab(t.value)}>
              <Icon name={t.icon} size={15} />{t.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn btn--ghost btn--sm" onClick={reload} title="새로고침"><Icon name="refresh" size={15} />새로고침</button>
      </div>

      {loading ? (
        <Loading title="설치 통계를 불러오는 중…" />
      ) : error ? (
        <ErrorState error={error} title="통계를 불러오지 못했습니다" onRetry={reload} onRelogin={logout} />
      ) : data && data.notReady ? (
        <EmptyState icon="chart" title="집계 백엔드가 아직 배포되지 않았습니다"
          sub="S3 액세스 로그 집계(infra/aws/install-stats)를 배포하면 여기에 버전별 다운로드가 표시됩니다." />
      ) : rows.length === 0 ? (
        <EmptyState icon="chart"
          title={isFw ? '펌웨어 다운로드 기록이 없습니다' : '앱 다운로드 기록이 없습니다'}
          sub="사용자가 설치파일을 내려받기 시작하면 버전별로 집계됩니다(집계까지 최대 수 시간 지연)." />
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
            <StatTile label="총 다운로드" value={nf.format(totals.sum)} sub={`${totals.versions}개 버전`} />
            <StatTile label="가장 최근 다운로드" value={fmtDateTime(totals.recent)} sub="설치 시도 기준 · KST" />
          </div>

          <div className="rows">
            {rows.map(r => {
              const dl = Number(r.downloads) || 0;
              const pct = maxDl > 0 ? Math.round((dl / maxDl) * 100) : 0;
              const key = isFw ? `${r.type}/${r.version}` : r.version;
              return (
                <div className="row" key={key} style={{ alignItems: 'center' }}>
                  <div className="row__main" style={{ minWidth: 0 }}>
                    <div className="row__title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {isFw && <span className="chip" style={{ background: '#eef5ff', color: '#2e6fff', fontWeight: 700 }}>{r.type}</span>}
                      <span className="mono">{r.version}</span>
                    </div>
                    <div className="row__sub" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span>최근 {fmtDateTime(r.last_at)}</span>
                      {r.first_at && <span style={{ color: 'var(--ink-400)' }}>· 처음 {fmtDateTime(r.first_at)}</span>}
                    </div>
                    {/* 다운로드 비율 막대(가장 많은 버전 기준) */}
                    <div style={{ marginTop: 8, height: 6, borderRadius: 999, background: 'var(--ink-100)', overflow: 'hidden', maxWidth: 340 }}>
                      <div style={{ width: pct + '%', height: '100%', background: 'var(--brand-500)', borderRadius: 999 }} />
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', paddingLeft: 12, flexShrink: 0 }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink-900)', lineHeight: 1 }}>{nf.format(dl)}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--ink-400)', fontWeight: 600, marginTop: 3 }}>다운로드</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="field__hint" style={{ marginTop: 16 }}>
            숫자는 <b>설치파일 다운로드 횟수</b>입니다 — 사람 수가 아니라 다운로드 단위(재시도·재설치 포함)이며,
            앱이 설치파일을 받은 시점을 "설치 시도"로 봅니다.
            {data && data.generated_at && <> · 집계 시각 {fmtDateTime(data.generated_at)}</>}
          </div>
        </>
      )}
    </div>
  );
}

export default InstallStatsAdmin;
